import { ReactNode, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

export interface Props {
  /** Fixed height of the paging area, in px. */
  height: number;
  /** Show the dot indicators below the carousel. */
  indicators?: boolean;
  sx?: SxProps<Theme>;
  children: ReactNode[];
}

const TRANSITION_MS = 500;

/**
 * A minimal paging carousel: one page visible at a time, cross-fading, with
 * previous/next buttons that reveal on hover and dot indicators underneath.
 *
 * Deliberately does not cycle -- the navigation buttons hide at the first and
 * last page rather than wrapping around.
 */
export default function Carousel({
  height,
  indicators = true,
  sx,
  children,
}: Props) {
  const [active, setActive] = useState(0);

  const pageCount = children.length;
  const lastIndex = Math.max(pageCount - 1, 0);

  // the tile recomputes its pages as it measures overflow, so the page count
  // can change underneath us; keep the active index addressable
  useEffect(() => {
    setActive((current) => Math.min(current, lastIndex));
  }, [lastIndex]);

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <Box sx={{ position: "relative", width: "100%", height }}>
        {children.map((child, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            aria-hidden={i !== active}
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              opacity: i === active ? 1 : 0,
              transition: `opacity ${TRANSITION_MS}ms ease-in-out`,
              // keep hidden pages out of the way of clicks and tab order
              visibility: i === active ? "visible" : "hidden",
            }}
          >
            {child}
          </Box>
        ))}
      </Box>

      {active > 0 && (
        <NavButton
          label="Previous"
          onClick={() => setActive((x) => Math.max(x - 1, 0))}
          sx={{ left: 0 }}
        >
          <NavigateBeforeIcon />
        </NavButton>
      )}

      {active < lastIndex && (
        <NavButton
          label="Next"
          onClick={() => setActive((x) => Math.min(x + 1, lastIndex))}
          sx={{ right: 0 }}
        >
          <NavigateNextIcon />
        </NavButton>
      )}

      {indicators && (
        <Box
          sx={{
            position: "absolute",
            bottom: "-1em",
            width: "100%",
            textAlign: "center",
          }}
        >
          {children.map((_, i) => (
            <IconButton
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              aria-label={`Page ${i + 1}`}
              onClick={() => setActive(i)}
              sx={{
                padding: 0,
                cursor: "pointer",
                transition: "200ms",
                color: i === active ? "#494949" : "#afafaf",
                "&:hover, &:active": {
                  color: i === active ? "#494949" : "#1f1f1f",
                },
              }}
            >
              <FiberManualRecordIcon sx={{ fontSize: "15px" }} />
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
}

function NavButton({
  label,
  onClick,
  sx,
  children,
}: {
  label: string;
  onClick: () => void;
  sx: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        height: "100px",
        top: "calc(50% - 70px)",
        zIndex: 1,
        "&:hover button, &:focus-within button": {
          backgroundColor: "black",
          filter: "brightness(120%)",
          opacity: 0.4,
        },
        ...sx,
      }}
    >
      <IconButton
        aria-label={label}
        onClick={onClick}
        sx={{
          margin: "0 10px",
          position: "relative",
          top: "calc(50% - 20px)",
          backgroundColor: "#494949",
          color: "white",
          fontSize: "30px",
          transition: "200ms",
          cursor: "pointer",
          opacity: 0,
          "&:hover": {
            opacity: "0.6 !important",
          },
        }}
      >
        {children}
      </IconButton>
    </Box>
  );
}
